import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  isLoading: false,
  list: [],
  total: 0,
  totalPages: 0,
  currentPage: 1,
  detail: {},
  filters: {
    page: 1,
    limit: 10,
    search: "",
    travelType: "",
    minRating: 0,
    latitude: null,
    longitude: null,
    radius: null,
  },
  selectedRowKeys: [],
  selectedRows: [],
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
};

const travelPlaceSlice = createSlice({
  name: "travelPlace",
  initialState,
  reducers: {
    // GET LIST
    getTravelPlaceListRequest: {
      reducer: (state) => ({
        ...state,
        isLoading: true,
        error: null,
      }),
      prepare: (payload) => ({ payload, meta: { thunk: true } }),
    },
    getTravelPlaceListSuccess: {
      reducer: (state, action) => {
        const { docs, totalDocs, totalPages, page } = action.payload;
        return {
          ...state,
          isLoading: false,
          list: docs || [],
          total: totalDocs || 0,
          totalPages: totalPages || 0,
          currentPage: page || 1,
          error: null,
        };
      },
      prepare: (payload, meta) => ({ payload, meta }),
    },
    getTravelPlaceListFailure: {
      reducer: (state, action) => ({
        ...state,
        isLoading: false,
        error: action.payload,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },

    // GET DETAIL
    getTravelPlaceDetailRequest: {
      reducer: (state) => ({
        ...state,
        isLoading: true,
        error: null,
      }),
      prepare: (payload) => ({ payload, meta: { thunk: true } }),
    },
    getTravelPlaceDetailSuccess: {
      reducer: (state, action) => ({
        ...state,
        isLoading: false,
        detail: action.payload,
        error: null,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },
    getTravelPlaceDetailFailure: {
      reducer: (state, action) => ({
        ...state,
        isLoading: false,
        error: action.payload,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },

    // CREATE
    createTravelPlaceRequest: {
      reducer: (state) => ({
        ...state,
        isCreating: true,
        error: null,
      }),
      prepare: (payload) => ({ payload, meta: { thunk: true } }),
    },
    createTravelPlaceSuccess: {
      reducer: (state) => ({
        ...state,
        isCreating: false,
        error: null,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },
    createTravelPlaceFailure: {
      reducer: (state, action) => ({
        ...state,
        isCreating: false,
        error: action.payload,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },

    // UPDATE
    updateTravelPlaceRequest: {
      reducer: (state) => ({
        ...state,
        isUpdating: true,
        error: null,
      }),
      prepare: (payload) => ({ payload, meta: { thunk: true } }),
    },
    updateTravelPlaceSuccess: {
      reducer: (state) => ({
        ...state,
        isUpdating: false,
        error: null,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },
    updateTravelPlaceFailure: {
      reducer: (state, action) => ({
        ...state,
        isUpdating: false,
        error: action.payload,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },

    // DELETE (SOFT)
    deleteTravelPlaceRequest: {
      reducer: (state) => ({
        ...state,
        isDeleting: true,
        error: null,
      }),
      prepare: (payload) => ({ payload, meta: { thunk: true } }),
    },
    deleteTravelPlaceSuccess: {
      reducer: (state) => ({
        ...state,
        isDeleting: false,
        error: null,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },
    deleteTravelPlaceFailure: {
      reducer: (state, action) => ({
        ...state,
        isDeleting: false,
        error: action.payload,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },

    // RESTORE
    restoreTravelPlaceRequest: {
      reducer: (state) => ({
        ...state,
        isUpdating: true,
        error: null,
      }),
      prepare: (payload) => ({ payload, meta: { thunk: true } }),
    },
    restoreTravelPlaceSuccess: {
      reducer: (state) => ({
        ...state,
        isUpdating: false,
        error: null,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },
    restoreTravelPlaceFailure: {
      reducer: (state, action) => ({
        ...state,
        isUpdating: false,
        error: action.payload,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },

    // SET FILTERS
    setTravelPlaceFilters: (state, action) => ({
      ...state,
      filters: {
        ...state.filters,
        ...action.payload,
      },
    }),

    // SET SELECTED
    setSelectedTravelPlaces: (state, action) => {
      const { selectedRowKeys, selectedRows } = action.payload;
      return {
        ...state,
        selectedRowKeys,
        selectedRows,
      };
    },

    // CLEAR FILTERS
    clearTravelPlaceFilters: (state) => ({
      ...state,
      filters: initialState.filters,
    }),

    // CLEAR ERROR
    clearTravelPlaceError: (state) => ({
      ...state,
      error: null,
    }),
  },
});

export const {
  getTravelPlaceListRequest,
  getTravelPlaceListSuccess,
  getTravelPlaceListFailure,
  getTravelPlaceDetailRequest,
  getTravelPlaceDetailSuccess,
  getTravelPlaceDetailFailure,
  createTravelPlaceRequest,
  createTravelPlaceSuccess,
  createTravelPlaceFailure,
  updateTravelPlaceRequest,
  updateTravelPlaceSuccess,
  updateTravelPlaceFailure,
  deleteTravelPlaceRequest,
  deleteTravelPlaceSuccess,
  deleteTravelPlaceFailure,
  restoreTravelPlaceRequest,
  restoreTravelPlaceSuccess,
  restoreTravelPlaceFailure,
  setTravelPlaceFilters,
  setSelectedTravelPlaces,
  clearTravelPlaceFilters,
  clearTravelPlaceError,
} = travelPlaceSlice.actions;

export default travelPlaceSlice.reducer;